import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty, keys, set } from 'lodash';
import { DateTime } from 'luxon';
import {
  Observable,
  catchError,
  combineLatest,
  concatMap,
  delay,
  filter,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AvatarNotificationModalActions } from '@components/avatar/notification-modal/notification-modal.actions';
import { AvatarNotificationModalComponent } from '@components/avatar/notification-modal/notification-modal.component';
import {
  AvatarNotificationService,
  ShouldShowResponse
} from '@components/avatar/notification-modal/notification-modal.service';
import { LWBHomeService } from '@components/lwb/home/home.service';
import { SkinPromptModalComponent } from '@components/report/skin/prompt-modal/prompt-modal.component';
import { ReportSkinService } from '@components/report/skin/skin.service';
import {
  CheckAvailabilityUserConsentResponse,
  DnaSkinFaceEligibilityResult,
  StatusEligibilitySkinFaceType
} from '@interfaces/schema';
import { QuestionsJournalModalComponent } from '@pages/daily/journal/questions-modal/questions-modal.component';
import { selectReports } from '@pages/report/report.selectors';
import { ReportSkinHealthGuidelineAnalysisActions } from '@pages/report/skin/health-guideline/analysis/analysis.actions';
import { QuestionnaireCheckResponseBody } from '../../providers/api/report-questionnaire/report-questionnaire.interface';
import { ReportQuestionnaireAPIService } from '../../providers/api/report-questionnaire/report-questionnaire.service';
import { EventService } from '../../providers/event/event.service';
import { Session } from '../../providers/session/session.service';
import { NourishQuestionnairePromptModalComponent } from '../questionnaire/nourish/prompt-modal/prompt-modal.component';
import { QuestionnaireQuickAccessModalBeingProcessedModalComponent } from '../questionnaire/quick-access-modal/being-processed/being-processed.component';
import { ReminderActions } from './reminder.actions';

/**
 * @description Reminder Component.
 * @exports
 * @class ReminderComponent
 */
@Component({
  selector: 'app-reminder',
  templateUrl: 'reminder.component.html',
  imports: [
    NourishQuestionnairePromptModalComponent,
    QuestionnaireQuickAccessModalBeingProcessedModalComponent,
    SkinPromptModalComponent,
    AvatarNotificationModalComponent,
    QuestionsJournalModalComponent
  ],
  providers: [
    ReportQuestionnaireAPIService,
    ReportSkinService,
    AvatarNotificationService,
    LWBHomeService
  ]
})
export class ReminderComponent implements OnInit, AfterViewInit {
  private readonly avatarNotificationService = inject(
    AvatarNotificationService
  );
  private readonly event = inject(EventService);
  private readonly homeService = inject(LWBHomeService);
  private readonly reportQuestionnaireAPIService = inject(
    ReportQuestionnaireAPIService
  );
  private readonly reportSkinService = inject(ReportSkinService);
  private readonly router = inject(Router);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  private readonly logger = Logger.getInstance(ReminderComponent.name);

  loading: boolean;
  pathname: string;

  routes: Record<string, Array<string>>;
  show: {
    reportNourishQuiz: boolean;
    reportNourishQuizCompleted: boolean;
    reportSkinFaceScan: boolean;
    questionsJournalModalOpen: boolean;
    avatarModalOpen: boolean;
  };

  skinReportStatus: string | null = null;
  status: {
    questionsJournalModalOpen: boolean;
    avatarModalOpen: boolean;
  };

  /**
   * Creates an instance of ReminderComponent.
   *
   * @memberof ReminderComponent
   */
  constructor() {
    this.loading = false;
    this.pathname = ``;

    this.show = {
      reportNourishQuiz: false,
      reportNourishQuizCompleted: false,
      reportSkinFaceScan: false,
      questionsJournalModalOpen: false,
      avatarModalOpen: false
    };
    this.routes = {
      reportNourishQuiz: [
        '/home',
        '/activity',
        '/tabs/home',
        '/tabs/activity',
        '/kit/activation/summary'
      ],
      reportSkinFaceScan: [
        '/home',
        '/activity',
        '/tabs/home',
        '/tabs/activity',
        '/kit/activation/summary'
      ],
      questionsJournalModalOpen: ['/home', '/tabs/home'],
      avatarModalOpen: ['/home', '/tabs/home']
    };
    this.status = {
      questionsJournalModalOpen: false,
      avatarModalOpen: false
    };
  }

  /**
   * @description Init.
   * @memberof ReminderComponent
   */
  ngOnInit(): void {
    this.fetch(window.location.pathname);

    this.subscribes();
  }

  /**
   * @description After init.
   * @memberof ReminderComponent
   */
  ngAfterViewInit(): void {
    this.store.select(selectReports).subscribe((reports) => {
      const skinReport = reports.find((obj) => obj.code === 'SKIN');
      this.skinReportStatus = skinReport?.availability as string;
    });

    this.router.events
      .pipe(
        filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe(() => {
        const pathname = window.location.pathname;

        if (this.pathname !== pathname) {
          this.init();
        }

        this.fetch(pathname);
      });
  }

  /**
   * @description Subscribes.
   * @memberof ReminderComponent
   */
  subscribes(): void {
    this.event
      .listen(`NOURISH_QUESTIONNAIRE` as any)
      .subscribe((value: boolean) => {
        if (this.show.reportNourishQuiz !== value) {
          this.show.reportNourishQuiz = value;
        }
      });

    this.event
      .listen(`NOURISH_QUESTIONNAIRE_COMPLETED` as any)
      .subscribe((value: boolean) => {
        if (this.show.reportNourishQuizCompleted !== value) {
          this.show.reportNourishQuizCompleted = value;
        }
      });

    // listen to event SKIN_FACE_SCAN here
    this.event.listen(`SKIN_FACE_SCAN` as any).subscribe((value: boolean) => {
      this.show.reportSkinFaceScan = value;
    });
  }

  /**
   * @description Fetch.
   * @param {string} pathname URL.
   * @memberof ReminderComponent
   */
  fetch(pathname: string): void {
    const logger = this.logger.child(this.fetch.name);

    this.pathname = pathname || window.location.pathname;

    if (this.loading) return;
    if (!this.session.isAuthenticated()) return;
    if (
      this.pathname !== '/' &&
      !keys(this.routes).some((key) =>
        this.routes[key].some((o) => this.pathname.includes(o))
      )
    ) {
      return;
    }

    of(true)
      .pipe(
        tap(() => (this.loading = true)),
        switchMap(() =>
          combineLatest([
            this.checkUserQuestionnaireReportNourish(),
            this.checkUserFaceScanReportSkin(),
            this.checkUserConsentDailyCheckIn(),
            this.checkAvatarNotificationModalOpen()
          ])
        ),
        delay(500)
      )
      .subscribe({
        /**
         * @description Next.
         * @param {[QuestionnaireCheckResponseBody | null, DnaSkinFaceEligibilityResult | null, CheckAvailabilityUserConsentResponse | null, ShouldShowResponse]} res Report Nourish Quiz, Face Scan.
         */
        next: ([reportNourishQuiz, faceScan, statusUserConsent, isShowAvatar]: [
          QuestionnaireCheckResponseBody | null,
          DnaSkinFaceEligibilityResult | null,
          CheckAvailabilityUserConsentResponse | null,
          ShouldShowResponse
        ]) => {
          // Set status for modals questions journal and avatar
          this.status = {
            questionsJournalModalOpen:
              statusUserConsent?.status === 'INCOMPLETE',
            avatarModalOpen: isShowAvatar.data.shouldShow
          };

          const queue = [];

          if (this.showReportNourishQuiz(reportNourishQuiz)) {
            if (!this.show.reportNourishQuiz) {
              queue.push(`reportNourishQuiz`);
            }
          }

          this.store.dispatch(
            ReminderActions.setFaceScanStatus({
              faceScanStatus: faceScan?.status ?? null
            })
          );
          this.store.dispatch(
            ReportSkinHealthGuidelineAnalysisActions.findLatestByUserResponse({
              data: faceScan?.latestDataSkinFaceAnalyzeProcess ?? null
            })
          );
          this.store.dispatch(
            ReminderActions.setLatestFaceScanProcess({
              latestFaceScanProcess:
                faceScan?.latestDataSkinFaceScanProcess ?? null
            })
          );

          if (
            this.skinReportStatus &&
            faceScan?.status &&
            !['NEED_TO_PURCHASE', 'NEED_TO_UNLOCK'].includes(
              this.skinReportStatus
            )
          ) {
            if (
              [
                StatusEligibilitySkinFaceType.FailedAnalyze,
                StatusEligibilitySkinFaceType.FailedScan,
                StatusEligibilitySkinFaceType.InProgressAnalyze,
                StatusEligibilitySkinFaceType.InProgressScan,
                StatusEligibilitySkinFaceType.NeedScan,
                StatusEligibilitySkinFaceType.SuccessAnalyze,
                StatusEligibilitySkinFaceType.SuccessScan
              ].includes(faceScan.status)
            ) {
              queue.push('reportSkinFaceScan');
            }
          }

          if (this.status.questionsJournalModalOpen) {
            if (this.show.avatarModalOpen) {
              logger.warn(
                'Avatar modal is open, skipping daily check-in modal.'
              );
            } else {
              queue.push(`questionsJournalModalOpen`);
            }
          } else if (this.status.avatarModalOpen) {
            if (this.show.questionsJournalModalOpen) {
              logger.warn(
                'Daily check-in modal is open, skipping avatar modal.'
              );
            } else {
              queue.push(`avatarModalOpen`);
            }
          } else {
            logger.warn('No modals to show for daily check-in or avatar.');
          }

          for (const [index, value] of queue.entries()) {
            setTimeout(
              () => {
                set(this.show, value, true);
              },
              index > 0 ? 1000 : 0
            );
          }

          this.loading = false;
        },
        /**
         * @description Error.
         */
        error: () => {
          this.loading = false;
        },
        /**
         * @description Complete.
         */
        complete: () => {
          this.loading = false;
        }
      });
  }

  /**
   * @description Check avatar notification modal open.
   * @returns {Observable<ShouldShowResponse>}
   * @memberof ReminderComponent
   */
  checkAvatarNotificationModalOpen(): Observable<ShouldShowResponse> {
    return of(true).pipe(
      concatMap(() => {
        if (
          this.pathname === '/' ||
          this.routes['avatarModalOpen'].some((o) => this.pathname.includes(o))
        ) {
          return this.avatarNotificationService.checkShouldShow();
        }

        return of({
          success: false,
          data: {
            shouldShow: false,
            reason: ''
          }
        });
      }),
      catchError(() =>
        of({
          success: false,
          data: {
            shouldShow: false,
            reason: ''
          }
        })
      )
    );
  }

  /**
   * @description Check user consent daily check-in.
   * @returns {Observable<CheckAvailabilityUserConsentResponse | null>}
   */
  checkUserConsentDailyCheckIn(): Observable<CheckAvailabilityUserConsentResponse | null> {
    const yesterday = DateTime.now().minus({ day: 1 }).toISODate();
    return of(true).pipe(
      concatMap(() => {
        if (
          this.pathname === '/' ||
          this.routes['questionsJournalModalOpen'].some((o) =>
            this.pathname.includes(o)
          )
        ) {
          return this.homeService.checkAvailability({
            date: yesterday,
            featureCode: 'DAILY_CHECK_IN'
          });
        }
        return of(null);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * @description Check user face scan status Report SKIN.
   * @returns {Observable<DnaSkinFaceEligibilityResult | null>}
   * @memberof ReminderComponent
   */
  checkUserFaceScanReportSkin(): import('rxjs').Observable<DnaSkinFaceEligibilityResult | null> {
    return of(true).pipe(
      map(() => this.session.getProfile()),
      concatMap((profile) => {
        if (
          this.pathname === '/' ||
          this.routes['reportSkinFaceScan'].some((o) =>
            this.pathname.includes(o)
          )
        ) {
          if (profile?.email) {
            const latestProcess =
              this.reportSkinService.findCheckEligibilitySkinFaceScanAndAnalyze();
            return latestProcess;
          }
        }

        return of(null);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * @description Check User Questionnaire Report Nourish.
   * @returns {import('rxjs').Observable<QuestionnaireCheckResponseBody | null>}
   * @memberof ReminderComponent
   */
  checkUserQuestionnaireReportNourish(): import('rxjs').Observable<QuestionnaireCheckResponseBody | null> {
    return of(true).pipe(
      map(() => this.session.getProfile()),
      concatMap((profile) => {
        if (
          this.pathname === '/' ||
          this.routes['reportNourishQuiz'].some((o) =>
            this.pathname.includes(o)
          )
        ) {
          if (profile?.email) {
            return this.reportQuestionnaireAPIService.checkUserQuestionnaireReport(
              { data: { type: 'NUTRIGENETIC', email: profile.email } }
            );
          }
        }

        return of(null);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Close questionnaire journal modal.
   */
  closeQuestionnaireJournalModal(): void {
    this.status.questionsJournalModalOpen = false;
    this.show.questionsJournalModalOpen = false;

    if (this.status.avatarModalOpen) {
      setTimeout(() => {
        this.show.avatarModalOpen = true;
      }, 1400);
    }
  }

  /**
   * Handle avatar notification modal close.
   *
   * @returns {void}
   * @memberof ReminderComponent
   */
  handleAvatarModalClose(): void {
    this.show.avatarModalOpen = false;
    this.status = {
      questionsJournalModalOpen: false,
      avatarModalOpen: false
    };
    this.store.dispatch(
      AvatarNotificationModalActions.setModalOpen({ isOpen: false })
    );
  }

  /**
   * @description Init.
   * @memberof ReminderComponent
   */
  init(): void {
    this.show = {
      reportNourishQuiz: false,
      reportNourishQuizCompleted: false,
      reportSkinFaceScan: false,
      avatarModalOpen: false,
      questionsJournalModalOpen: false
    };
  }

  /**
   * @description Show report nourish quiz state.
   * @param {QuestionnaireCheckResponseBody} payload Data.
   * @returns {boolean}
   * @memberof ReminderComponent
   */
  showReportNourishQuiz(
    payload: QuestionnaireCheckResponseBody | null
  ): boolean {
    if (!isEmpty(payload)) {
      if (payload.data) {
        if (payload.data === 'PENDING') {
          return true;
        }
      }
    }

    return false;
  }
}
